import Button from '@jbhi-fi/lanyard-ui/components/Button/Button';
import DeleteIcon from '@jbhi-fi/lanyard-ui/components/Icons/Delete';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint/useBreakpoint';
import React, { useEffect, useRef, useState } from 'react';
import { styles } from './ProductCompareBar.css';
import { useProductCompare } from '../context/ProductCompareProvider';
import { ProductCompareThumbnail } from './ProductCompareThumbnail/ProductCompareThumbnail';
import ArrowIcon from '@jbhi-fi/lanyard-ui/components/Icons/Arrow';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert/Alert';
import { createPortal } from 'react-dom';
import { submitTracking } from '../../Tracking/utils/shopifyActionTrackingHelper';

export interface CompareProduct {
  sku: string;
  imageUrl: string;
}

export const ProductCompareBar = ({ noPortal }: { noPortal?: boolean }) => {
  if (noPortal) {
    return <BaseProductCompareBar noPortal />;
  }
  return createPortal(<BaseProductCompareBar />, document.body);
};

const BaseProductCompareBar = ({ noPortal = false }: { noPortal?: boolean }) => {
  const isDesktop = useBreakpoint('md', 'up');
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    itemsToCompare,
    compareSelectedItems,
    clearAllComparedItems,
    isCompareLimitReached,
    setIsCompareLimitReached,
  } = useProductCompare();

  const hasRendered = useRef(false);

  useEffect(() => {
    if (isCompareLimitReached) {
      setIsExpanded(true);
    }
  }, [isCompareLimitReached]);

  useEffect(() => {
    if (hasRendered.current) {
      setIsExpanded(true);
    } else {
      hasRendered.current = true;
    }
  }, [itemsToCompare]);

  const shouldShowCompareButton = itemsToCompare.length >= 2;

  const handleExpand = () => {
    submitTracking({
      event: 'productCompare',
      action: `compare-module-${isExpanded ? 'collapse' : 'expand'}`,
      trackingDescriptor: { page: 'compareBar' },
      gtm: {},
    });
    setIsExpanded(prev => !prev);
  };

  return (
    <span id="compare-bar" className={styles.compareBarWrapper({ noPortal })}>
      {itemsToCompare.length < 1 ? null : (
        <div>
          {isCompareLimitReached ? (
            <div className={styles.alertContainer}>
              <Alert
                onClose={() => setIsCompareLimitReached(false)}
                showIcon
                closable
                classes={styles.alert}
                heading="Compare limit reached"
                content="You can only compare a maximum of 4 products. To compare this product, please remove another first."
                type="warning"
              ></Alert>
            </div>
          ) : null}
          {!isDesktop ? (
            <div className={styles.toggleContainer({ isExpanded })}>
              <Button
                type="linkWithIcon"
                icon={<ArrowIcon style={{ scale: isExpanded ? '1' : '-1' }} />}
                iconPosition="left"
                onClick={handleExpand}
                label="Compare"
                data-testid="compare-bar-button"
              />
            </div>
          ) : null}
          {isExpanded || isDesktop ? (
            <div className={styles.mainContainer}>
              <div className={styles.itemsContainer}>
                {itemsToCompare.map((item, i) => (
                  <ProductCompareThumbnail product={item} key={i} />
                ))}
                {[...Array(4 - itemsToCompare.length)].map((_, i) => (
                  <div className={styles.itemPlaceholder} key={i} />
                ))}
              </div>
              <div>
                {shouldShowCompareButton ? (
                  <div className={styles.actionContainer}>
                    <Button
                      type="action"
                      size={isDesktop ? undefined : 'sm'}
                      onClick={compareSelectedItems}
                      data-testid="compare-bar-button"
                    >
                      Compare
                    </Button>
                    {isDesktop ? (
                      <Button
                        type="linkWithIcon"
                        label="Clear all"
                        iconPosition="left"
                        icon={<DeleteIcon width={20} height={20} />}
                        onClick={clearAllComparedItems}
                      />
                    ) : null}
                  </div>
                ) : (
                  <span className={styles.text}>Add another product to start comparing</span>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </span>
  );
};
